import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { request } from "@/api/http";

interface PrefsResponse {
  data: Record<string, unknown>;
}

const KEY = ["preferences"];

export function usePreferences() {
  const qc = useQueryClient();
  const q = useQuery<PrefsResponse>({
    queryKey: KEY,
    queryFn: () => request<PrefsResponse>("/api/v1/me/preferences"),
    staleTime: 60_000,
  });

  const patchMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      request<PrefsResponse>("/api/v1/me/preferences", { method: "PATCH", body: { data: patch } }),
    onSuccess: (next) => qc.setQueryData(KEY, next),
  });

  /**
   * Persist a slice of the user's preferences. Calls land on the
   * backend with `PATCH` (shallow merge), so concurrent slices don't
   * stomp each other.
   */
  const patch = useCallback(
    (slice: Record<string, unknown>) => patchMutation.mutate(slice),
    [patchMutation],
  );

  return {
    data: q.data?.data ?? {},
    isLoading: q.isLoading,
    patch,
  };
}
